import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/customer/components/ui/button'
import { cn } from '@/customer/lib/utils'

/**
 * Poster-outline dialog primitive. Wraps Base UI's `Dialog`.
 *
 * Structure convention: `DialogContent` owns the outer frame + close
 * button but has no padding. Each section (`DialogHeader`,
 * `DialogBody`, `DialogFooter`) supplies its own padding, so a
 * coloured `DialogHeader` can bleed edge-to-edge without any
 * negative-margin acrobatics.
 *
 * Header tones are cva-driven. `neutral` (default) draws a simple
 * bottom border; brand tones fill the strip with the matching brand
 * colour, mirroring the branded modal treatment used on the account
 * home.
 */

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 isolate z-50 bg-black/30 duration-100 supports-backdrop-filter:backdrop-blur-sm data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  // Focus the popup itself on open rather than the first tabbable
  // child. Base UI does the latter by default, which means an inner
  // Cancel/Close button lights up with the focus ring the moment the
  // dialog appears. Focusing the popup keeps keyboard trap semantics
  // (Tab still cycles through inner controls) without the initial
  // ring flash. `tabIndex={-1}` makes the popup itself focusable.
  const popupRef = React.useRef<HTMLDivElement>(null)
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        ref={popupRef}
        initialFocus={popupRef}
        tabIndex={-1}
        data-slot="dialog-content"
        className={cn(
          'fixed top-1/2 left-1/2 z-50 flex w-full max-w-[calc(100%-2rem)] max-h-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl md:rounded-2xl bg-background text-body-md duration-100 outline-none sm:max-w-lg data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                className="absolute top-3 right-3 z-10 hover:bg-black/10"
              />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

const dialogHeaderVariants = cva(
  'flex items-center gap-3 px-4 sm:px-6 py-4 [&_[data-slot=dialog-title]]:text-headline-sm',
  {
    variants: {
      tone: {
        neutral: 'border-b border-border',
        yellow: 'bg-yellow',
        blue: 'bg-blue',
        pink: 'bg-pink',
        'baby-blue': 'bg-baby-blue',
        green: 'bg-green',
        orange: 'bg-orange',
      },
    },
    defaultVariants: { tone: 'neutral' },
  }
)

export type DialogHeaderTone = NonNullable<VariantProps<typeof dialogHeaderVariants>['tone']>

function DialogHeader({
  className,
  tone,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof dialogHeaderVariants>) {
  return (
    <div data-slot="dialog-header" className={cn(dialogHeaderVariants({ tone }), className)} {...props} />
  )
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-body"
      className={cn('flex flex-col gap-4 sm:gap-6 overflow-y-auto p-4 sm:p-6', className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-3 border-t border-border p-4 sm:p-6 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('leading-none', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-body-md text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
